import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone } from "lucide-react";
import { canonical, faqPageSchema } from "@/lib/seo";

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "Frequently Asked Questions - Malak BnB Resort" },
      { name: "description", content: "Answers to common questions about Malak BnB Resort in Crystal Beach, Ontario: location, rooms, booking, breakfast, the pool, and events." },
      { property: "og:title", content: "Frequently Asked Questions - Malak BnB Resort" },
      { property: "og:description", content: "Location, rooms, booking, breakfast, the pool, and events at Malak BnB Resort in Crystal Beach, Ontario." },
      { property: "og:url", content: "https://malakbnbresort.ca/faq" },
      { property: "og:image", content: "/images/hero-exterior.webp" },
      { "script:ld+json": faqPageSchema(ALL_FAQ) },
    ],
    links: [canonical("/faq")],
  }),
  component: FaqPage,
});

const LOCATION_FAQ = [
  {
    q: "Where is Malak BnB Resort located?",
    a: "Malak BnB Resort is located at 264 Ridge Road South in Crystal Beach, Ontario. Crystal Beach is a small lakeside community in the Fort Erie area of the Niagara Region, roughly 30 minutes from Niagara Falls and about 15 minutes from the Peace Bridge crossing into Buffalo, New York.",
  },
  {
    q: "How close is Malak BnB Resort to Bay Beach and Crystal Beach?",
    a: "Malak BnB Resort is a 5-minute walk from Bay Beach in Crystal Beach, Ontario. The town centre and several restaurants are also within easy walking distance of the property.",
  },
  {
    q: "Is Malak BnB Resort close to Niagara Falls?",
    a: "Malak BnB Resort is approximately 30 minutes by car from Niagara Falls. Many guests use Crystal Beach as a quieter, more affordable base for a Niagara Falls day trip.",
  },
  {
    q: "Is Malak BnB Resort cycling-friendly?",
    a: "Yes. Malak BnB Resort is in a cycling-friendly part of the Niagara Region, close to Cycle and Stay Niagara routes, with flat roads connecting toward Fort Erie and Ridgeway.",
  },
];

const AMENITIES_FAQ = [
  {
    q: "Does Malak BnB Resort have a pool?",
    a: "Yes. Malak BnB Resort has an outdoor heated swimming pool, along with indoor and outdoor lounge areas for guests to relax in.",
  },
  {
    q: "Is breakfast included at Malak BnB Resort?",
    a: "Yes. A continental breakfast buffet is included with every stay at Malak BnB Resort.",
  },
  {
    q: "Is parking available at Malak BnB Resort?",
    a: "Yes. Free parking is available on site for guests staying at Malak BnB Resort.",
  },
  {
    q: "Can Malak BnB Resort host small events or gatherings?",
    a: "Yes. Malak BnB Resort hosts small events and private gatherings for up to 25 guests, including weddings by the pool, birthdays, graduations, and corporate retreats, with in-house catering available.",
  },
];

const BOOKING_FAQ = [
  {
    q: "How do I request a reservation at Malak BnB Resort?",
    a: "Malak BnB Resort takes direct reservation requests. Submit a request with your preferred room and dates through the booking form, and the team personally reviews and confirms your stay before sending payment details. There is no instant booking.",
  },
  {
    q: "Is a deposit required to book at Malak BnB Resort?",
    a: "A $50.00 deposit is required for any incidentals, and full payment and deposit details are shared once your reservation request is confirmed. There are no third-party booking fees.",
  },
  {
    q: "What is the cancellation policy at Malak BnB Resort?",
    a: "Malak BnB Resort gives a full refund for cancellations made 7 days or more before the arrival time, and a 50% refund of the total reservation rate before tax for cancellations made 72 hours or more before arrival. No refund is issued within 48 hours of arrival. Cancellations are accepted by email or text message.",
  },
  {
    q: "What rooms are available at Malak BnB Resort?",
    a: "Malak BnB Resort offers four room types: 2 Double Beds (450 sq ft, from C$345), Standard Double Bed (400 sq ft, from C$345), Deluxe Queen Bed (400 sq ft, from C$355), and Queen Bed Suite (440 sq ft, from C$380). All rooms include air conditioning, flat screen TV, free Wi-Fi, and an in-room safe.",
  },
  {
    q: "Is Malak BnB Resort family-owned?",
    a: "Yes. Malak BnB Resort is a family-owned and operated bed and breakfast in Crystal Beach, Ontario.",
  },
];

const ALL_FAQ = [...LOCATION_FAQ, ...AMENITIES_FAQ, ...BOOKING_FAQ];

function FaqSection({ title, items }: { title: string; items: typeof LOCATION_FAQ }) {
  return (
    <div>
      <h2 className="text-2xl font-heading text-foreground mb-8">{title}</h2>
      <div className="space-y-8">
        {items.map(({ q, a }) => (
          <div key={q} className="border-b border-border pb-8 last:border-0 last:pb-0">
            <h3 className="font-heading text-lg text-foreground mb-3">{q}</h3>
            <p className="text-muted-foreground leading-relaxed text-sm">{a}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function FaqPage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative h-[45vh] min-h-[300px] overflow-hidden">
        <img
          src="/images/hero-exterior.webp"
          alt="Exterior of Malak BnB Resort in Crystal Beach, Ontario"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/65 via-foreground/20 to-transparent" />
        <div className="relative z-10 flex h-full items-end pb-14 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl w-full">
            <p className="text-white/70 font-semibold text-sm uppercase tracking-widest mb-3">Malak BnB Resort</p>
            <h1 className="text-4xl sm:text-5xl font-heading text-white leading-tight">
              Frequently Asked Questions
            </h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-muted-foreground leading-relaxed">
            Answers to the questions guests ask most often about staying at Malak BnB Resort in Crystal Beach,
            Ontario. For anything not covered here, use the{" "}
            <Link to="/contact" className="text-primary font-semibold hover:underline">
              contact page
            </Link>{" "}
            and the team will get back to you directly.
          </p>
        </div>
      </section>

      {/* FAQ sections */}
      <section className="pb-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 space-y-16">
          <FaqSection title="Location and Getting Here" items={LOCATION_FAQ} />
          <FaqSection title="Amenities" items={AMENITIES_FAQ} />
          <FaqSection title="Rooms and Booking" items={BOOKING_FAQ} />
        </div>
      </section>

      {/* Internal links */}
      <section className="pb-4 bg-card">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center pt-4 pb-16 flex flex-wrap justify-center gap-x-8 gap-y-3">
          <Link to="/reservation-details" className="text-primary font-semibold text-sm hover:underline">
            How direct booking works →
          </Link>
          <Link to="/terms-and-cancellation-policy" className="text-primary font-semibold text-sm hover:underline">
            Cancellation policy →
          </Link>
          <Link to="/rooms" className="text-primary font-semibold text-sm hover:underline">
            View rooms and rates →
          </Link>
          <Link to="/explore" className="text-primary font-semibold text-sm hover:underline">
            Explore Crystal Beach →
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-foreground">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <p className="text-primary-foreground/50 font-semibold text-sm uppercase tracking-widest mb-4">Crystal Beach, Ontario</p>
          <h2 className="text-3xl sm:text-4xl font-heading text-primary-foreground leading-tight">
            Still have a question?
          </h2>
          <p className="mt-5 text-primary-foreground/70 max-w-lg mx-auto leading-relaxed">
            Submit a reservation request and the team will personally confirm availability and answer anything else along the way.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/book" className="inline-flex items-center justify-center rounded-full bg-primary px-10 py-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg">
              Request to Book
            </Link>
            <a href="tel:+12899298055" className="inline-flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground text-sm font-medium transition-colors">
              <Phone className="h-4 w-4" />
              1-289-929-8055
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
